import { useEffect, useState } from 'react';

import { realtime } from './ws';

/** One round's numbers, waiting for or having its moment on screen. */
export interface Reveal {
  drawNo: number;
  periodCode: string;
  results: number[];
}

/** How long between one number landing and the next. */
const STEP_MS = 240;
/** How long the completed set is held before the card goes back to counting. */
const HOLD_MS = 2000;

/**
 * Turns `draw.result` into a reveal that plays out, one round at a time.
 *
 * The event carries every number at once, so without this the numbers appear
 * in a single frame — which reads as the page re-rendering rather than as a
 * round finishing. Here they are dealt out a beat apart, position 1 first.
 *
 * Reveals **queue**. Normally there is only ever one round to show, but the
 * scheduler can have a backlog to drain after downtime, and two results landing
 * a second apart would otherwise interrupt each other and show neither: the
 * first set would be replaced mid-animation by the second, and an operator would
 * see numbers flicker past for rounds they never got to read. Each one waits for
 * the one before it to finish. The server paces its own reveals to match, so the
 * queue stays short rather than accumulating a lag the operator has to sit
 * through.
 */
export function useResultReveal() {
  const [queue, setQueue] = useState<Reveal[]>([]);
  const [reveal, setReveal] = useState<Reveal | null>(null);
  /** How many of the active reveal's numbers have landed so far. */
  const [shown, setShown] = useState(0);

  useEffect(
    () =>
      realtime.subscribe((event, data) => {
        if (event !== 'draw.result') return;
        const results = data.results as number[] | undefined;
        if (!Array.isArray(results) || results.length === 0) return;

        const next: Reveal = {
          drawNo: Number(data.drawNo),
          periodCode: String(data.periodCode ?? ''),
          results,
        };

        setQueue((pending) =>
          // A round is shown once. `draw.result` and the settlement that follows
          // both describe the same round, and a reconnect can re-deliver.
          pending.some((item) => item.drawNo === next.drawNo) ? pending : [...pending, next],
        );
      }),
    [],
  );

  // Take the next round off the queue, but only once the screen is free.
  useEffect(() => {
    if (reveal || queue.length === 0) return;
    setReveal(queue[0]);
    setShown(0);
    setQueue((pending) => pending.slice(1));
  }, [reveal, queue]);

  // Deal the numbers out, then hold the finished set, then stand down.
  useEffect(() => {
    if (!reveal) return;

    if (shown < reveal.results.length) {
      const timer = window.setTimeout(() => setShown((count) => count + 1), STEP_MS);
      return () => clearTimeout(timer);
    }

    const timer = window.setTimeout(() => setReveal(null), HOLD_MS);
    return () => clearTimeout(timer);
  }, [reveal, shown]);

  return {
    reveal,
    /** Numbers landed so far — the rest are not rendered yet. */
    shown,
    /** True while a set is still landing, as opposed to being held. */
    running: Boolean(reveal) && shown < (reveal?.results.length ?? 0),
    /** Rounds still waiting their turn, so the card can say so. */
    waiting: queue.length,
  };
}
